import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Trash2 } from 'lucide-react';
import { z } from 'zod';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { optionalInput } from '@/common/form/optionalInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { SelectItem } from '@/common/components/ui/select';
import { organizationQueries, organizationQueryKeys } from '@/features/organizations/queries';
import { OrganizationService } from '@/features/organizations/services/OrganizationService';
import { useCountries } from '@/features/auth/hooks/useCountries';
import { useConfirmDialog } from '@/common/components/confirm-dialog/useConfirmDialog';
import { useCurrentUser } from '@/features/auth/hooks/useCurrentUser';
import type { Organization } from '@/features/organizations/services/schemas';

const updateOrgSchema = z.object({
  name: requiredInput(z.string().min(1)),
  street: optionalInput(z.string()).transform((v) => v ?? ''),
  city: optionalInput(z.string()).transform((v) => v ?? ''),
  zipCode: optionalInput(z.string()).transform((v) => v ?? ''),
  country: requiredInput(z.string()),
});

type Props = {
  org: Organization;
  orgId: number;
};

export function SettingsTab({ org, orgId }: Props) {
  const queryClient = useQueryClient();
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const { data: members } = useSuspenseQuery(organizationQueries.members(orgId));
  const currentUser = useCurrentUser();
  const isAdmin = members.find((m) => m.user.email === currentUser.email)?.role === 'full';
  const countries = useCountries();
  const orgName = org.name;

  const form = useAppForm(
    appFormOptions({
      schema: updateOrgSchema,
      defaultValues: {
        name: org.name,
        street: org.street,
        city: org.city,
        zipCode: org.zipCode,
        country: org.country,
      },
      onSubmit: async ({ value }) => {
        await OrganizationService.update(orgId, value);
        await queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all });
      },
    })
  );

  const deleteMutation = useMutation({
    mutationFn: () => OrganizationService.delete(orgId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all });
      navigate({ to: '/organizations' });
    },
  });

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t`Delete organization?`,
      description: t`"${orgName}" will be permanently deleted along with all members and access.`,
      confirmLabel: t`Delete organization`,
    });
    if (confirmed) deleteMutation.mutate();
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div className="rounded-2xl border border-border bg-card p-6">
        <form.AppForm>
          <form.FormElement>
            <div className="space-y-4">
              <FormErrors />

              <form.AppField name="name">
                {(field) => <field.TextField label={<Trans>Company name</Trans>} required />}
              </form.AppField>

              <form.AppField name="street">
                {(field) => <field.TextField label={<Trans>Street and number</Trans>} />}
              </form.AppField>

              <div className="grid grid-cols-2 gap-4">
                <form.AppField name="city">
                  {(field) => <field.TextField label={<Trans>City</Trans>} />}
                </form.AppField>

                <form.AppField name="zipCode">
                  {(field) => <field.TextField label={<Trans>Postal code</Trans>} />}
                </form.AppField>
              </div>

              <form.AppField name="country">
                {(field) => (
                  <field.SelectField label={<Trans>Country</Trans>}>
                    {countries.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </field.SelectField>
                )}
              </form.AppField>

              <FormSubmit>
                <Trans>Save changes</Trans>
              </FormSubmit>
            </div>
          </form.FormElement>
        </form.AppForm>
      </div>

      {isAdmin && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5">
          <h4 className="text-sm font-semibold text-destructive mb-1">
            <Trans>Danger zone</Trans>
          </h4>
          <p className="text-sm text-destructive/80 mb-4">
            <Trans>
              Deleting the organization will remove all members, billing addresses, and access. This
              action is irreversible.
            </Trans>
          </p>
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/40 bg-background px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
          >
            <Trash2 size={14} />
            <Trans>Delete organization</Trans>
          </button>
        </div>
      )}
    </div>
  );
}
